import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Form, Alert } from 'react-bootstrap';
import Breadcrumbs, { BreadcrumbItem } from '../../../Common/Breadcrumbs';
import { getProductById, createProduct, updateProduct } from '../../../api/shop/products/productService';
import { DetailedProduct, CreateProductRequest, UpdateProductRequest } from '../../../api/shop/types/productTypes';
import { SellerSuccessModal } from '../Sellers/components/SellerSuccessModal';
import ProductBasicInfo from './components/ProductBasicInfo';
import ProductVariants from './components/ProductVariants';
import ProductSellerLocations from './components/ProductSellerLocations';
import ProductImageUpload from './components/ProductImageUpload';
import ProductVisibility from './components/ProductVisibility';
import { CatalogueConfigProvider, useCatalogueConfig } from '../../../contexts/CatalogueConfigContext';
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';
import './ProductDetail.css';
import '../Sellers/SellerDetail.css';

const ProductDetailContent: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { config } = useCatalogueConfig();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'shop.admin',
    'shop.product.manage',
  ]);

  // Determine mode based on URL
  const isAddMode = !id || id === 'add';
  const isEditMode = Boolean(id && id !== 'add' && window.location.pathname.includes('/edit'));
  const isViewMode = Boolean(id && id !== 'add' && !window.location.pathname.includes('/edit'));

  // State management
  const [product, setProduct] = useState<DetailedProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [touchedFields, setTouchedFields] = useState<string[]>([]);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form data state
  const [formData, setFormData] = useState<Partial<DetailedProduct>>({
    seller: '',
    sport: 'Football',
    category: 'Shoes',
    gender: 'UNISEX',
    name: '',
    description: '',
    pricing: {
      mrp: 0,
      sellingPrice: 0,
      stockQty: 1,
    },
    badges: [],
    colorImages: {},
    images: [],
    variants: [],
    fulfillmentCapabilities: ['PICKUP'],
    status: 'ACTIVE',
    additionalInfo: {
      productDesc: '',
      material: '',
      misc: '',
    },
    sellerLocations: [],
    estimatedPickupDays: 5,
    visibility: {
      isFeatured: false,
      featuredRank: 1,
    },
  });

  // Derive whether the selected category is a sports nutrition category
  const isSportsNutrition = useMemo(() => {
    if (!config) return false;
    const snValues = (config.categories.sportsNutrition || []).map(c => c.value);
    return snValues.includes(formData.category || '');
  }, [config, formData.category]);

  // Load product data for edit/view modes
  useEffect(() => {
    const loadProduct = async () => {
      if (isAddMode) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const productData = await getProductById(id!);
        setProduct(productData);
        setFormData(productData);
        setError(null);
      } catch (err) {
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, isAddMode]);

  // Handle form data changes
  const handleFormChange = (updates: Partial<DetailedProduct>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  // Handle field touch (when user interacts with a field)
  const handleFieldTouch = (fieldName: string) => {
    setTouchedFields(prev => prev.includes(fieldName) ? prev : [...prev, fieldName]);
  };

  // Validate required fields
  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formData.seller || formData.seller.trim() === '') errors.push('Seller is required');
    if (!formData.name || formData.name.trim() === '') errors.push('Product name is required');
    if (!formData.category) errors.push('Category is required');
    if (!formData.pricing?.mrp || formData.pricing.mrp <= 0) errors.push('Product MRP must be greater than 0');
    if (!formData.pricing?.sellingPrice || formData.pricing.sellingPrice <= 0) errors.push('Selling price must be greater than 0');
    if (!formData.description || formData.description.trim() === '') errors.push('Product description is required');
    const isExternalAds = formData.fulfillmentCapabilities?.includes('EXTERNAL_ADS');
    if (!isExternalAds && (!formData.sellerLocations || formData.sellerLocations.length === 0)) errors.push('At least one seller location is required');
    if (!isSportsNutrition && (!formData.variants || formData.variants.length === 0)) errors.push('At least one product variant is required');

    if (errors.length > 0) {
      setError(`Validation failed: ${errors.join(', ')}`);
    }

    return errors.length === 0;
  };

  // Handle save (create or update)
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      setShowValidation(true);

      if (!validateForm()) {
        setSaving(false);
        return;
      }

      const payload = { ...formData };
      if (isSportsNutrition) delete payload.sport;

      if (isAddMode) {
        await createProduct(payload as CreateProductRequest);
        setSuccessTitle('Product Added Successfully!');
        setSuccessMessage(`${formData.name || 'Product'} has been added successfully.`);
        setShowSuccessModal(true);
      } else {
        await updateProduct(id!, { ...payload, _id: id! } as UpdateProductRequest);
        setSuccessTitle('Product Updated Successfully!');
        setSuccessMessage(`${formData.name || 'Product'} has been updated successfully.`);
        setShowSuccessModal(true);
      }
    } catch (err) {
      setError('Failed to save product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    navigate(`/shop-services/products/${id}/edit`);
  };

  const handleCancel = () => {
    if (isAddMode) {
      navigate('/shop-services/products');
    } else {
      navigate(`/shop-services/products/${id}`);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    if (isAddMode) {
      navigate('/shop-services/products');
    } else {
      navigate(`/shop-services/products/${id}`);
    }
  };

  const getPageTitle = () => {
    if (isAddMode) return 'Add Product';
    if (isEditMode) return 'Edit Product';
    return 'Product Details';
  };

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [
      { label: 'Shop Services', onClick: () => navigate('/shop-services/products') },
      { label: 'Products', onClick: () => navigate('/shop-services/products') },
    ];

    if (isAddMode) {
      items.push({ label: 'Add Product', active: true });
    } else if (isEditMode) {
      items.push(
        { label: product?.name || 'Product', onClick: () => navigate(`/shop-services/products/${id}`) },
        { label: 'Edit', active: true }
      );
    } else {
      items.push({ label: product?.name || 'Product Details', active: true });
    }

    return items;
  };

  if (loading) {
    return (
      <div className="page-content seller-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content seller-detail-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={getBreadcrumbItems()} />
        </div>

        {/* Page Header with Back Button */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center" style={{ gap: '16px' }}>
              <button
                className="back-button"
                onClick={() => navigate('/shop-services/products')}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <h1 className="page-title mb-0">{getPageTitle()}</h1>
            </div>
            <div className="d-flex gap-3">
              {isViewMode && hasManagePermissions && (
                <Button variant="primary" onClick={handleEdit}>
                  Edit Product <i className="ri-edit-line ms-1"></i>
                </Button>
              )}
              {(isAddMode || isEditMode) && (
                <>
                  <Button variant="outline-secondary" onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        {isAddMode ? 'Add Product' : 'Save Changes'} +
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </div>
        )}

        {/* Main Content */}
        <div style={{ padding: '0 15px' }}>
          <Row>
            <Col xl={12}>
              {/* Basic Information */}
              <Card className="mb-4">
                <Card.Body>
                  <ProductBasicInfo
                    data={formData}
                    onChange={handleFormChange}
                    onFieldTouch={handleFieldTouch}
                    isReadOnly={isViewMode}
                    showValidation={showValidation}
                    touchedFields={touchedFields}
                    isSportsNutrition={isSportsNutrition}
                  />
                </Card.Body>
              </Card>

              {/* Customize Variants */}
              <Card className="mb-4">
                <Card.Body>
                  <ProductVariants
                    data={formData}
                    onChange={handleFormChange}
                    isReadOnly={isViewMode}
                    isSportsNutrition={isSportsNutrition}
                  />
                </Card.Body>
              </Card>

              {/* Seller Selection & Locations */}
              <Card className="mb-4">
                <Card.Body>
                  <ProductSellerLocations
                    data={formData}
                    onChange={handleFormChange}
                    onFieldTouch={handleFieldTouch}
                    isReadOnly={isViewMode}
                    showValidation={showValidation}
                    touchedFields={touchedFields}
                  />
                </Card.Body>
              </Card>

              {/* Image Upload */}
              <Card className="mb-4">
                <Card.Body>
                  <ProductImageUpload
                    data={formData}
                    onChange={handleFormChange}
                    isReadOnly={isViewMode}
                    isSportsNutrition={isSportsNutrition}
                  />
                </Card.Body>
              </Card>

              {/* Product Visibility */}
              <Card className="mb-4">
                <Card.Body>
                  <ProductVisibility
                    data={formData}
                    onChange={handleFormChange}
                    isReadOnly={isViewMode}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Success Modal */}
      <SellerSuccessModal
        show={showSuccessModal}
        handleClose={handleSuccessModalClose}
        title={successTitle}
        message={successMessage}
        imageName="admin/static-assets/shop-services/product-added.webp"
        buttonLabel="Continue"
      />
    </div>
  );
};

const ProductDetail: React.FC = () => (
  <CatalogueConfigProvider>
    <ProductDetailContent />
  </CatalogueConfigProvider>
);

export default ProductDetail;
